import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PiFileTextLight, PiHandshakeLight, PiUsersThreeLight } from 'react-icons/pi';
import { useAppDispatch } from '../store';
import { showModal } from '../store/actions/modalActions';
import { showFeedbackModal } from '../store/actions/UserFeedbackActions';
import DetailFrame from '../components/shared/Frames/DetailFrame';
import GlassCard from '../components/shared/brand/GlassCard';
import { InfoRow, Well } from '../components/shared/brand/Field';
import Button from '../components/shared/TailwindComponents/Button';
import Skeleton from '../components/shared/TailwindComponents/Skeleton';
import EmptyState from '../components/shared/TailwindComponents/EmptyState';
import MarketplaceGate from '../components/Marketplace/MarketplaceGate';
import TriageSummary from '../components/Marketplace/TriageSummary';
import EngagementConversation from '../components/Marketplace/EngagementConversation';
import EngagementDocuments from '../components/Marketplace/EngagementDocuments';
import EngagementLetter from '../components/Marketplace/EngagementLetter';
import { ClientProposalActions, ProposalHistory, isLive } from '../components/Marketplace/ProposalNegotiation';
import { enumLabel } from '../components/Marketplace/marketplaceLabels';
import {
  LeadStatusBadge,
  ProposalStatusBadge,
  Section,
  fmtDate,
  fmtProposalPrice,
  leadTitle,
} from '../components/Marketplace/marketplaceUi';
import {
  Lead,
  Match,
  Proposal,
  EngagementView,
  acceptProposal,
  errorMessage,
  getEngagement,
  getLead,
  getMatches,
  listProposals,
} from '../services/api/marketplaceService';

const fmtSize = (bytes: number): string =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/** Client side: one request - what I asked, the triage, who fits, who proposed. */
const RequestDetailContent: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { leadUuid } = useParams<{ leadUuid: string }>();

  const [lead, setLead] = useState<Lead | null | undefined>(undefined);
  const [matches, setMatches] = useState<Match[] | null>(null);
  const [proposals, setProposals] = useState<Proposal[] | null>(null);
  const [accepting, setAccepting] = useState<string | null>(null);

  const fail = useCallback(
    (err: unknown, fallback: string) =>
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', duration: 4000, message: errorMessage(err, fallback) })),
    [dispatch]
  );

  const load = useCallback(async () => {
    try {
      const l = await getLead(leadUuid);
      setLead(l);
      if (!l.triage) {
        setMatches([]);
        setProposals([]);
        return;
      }
      const [m, p] = await Promise.all([getMatches(leadUuid), listProposals(leadUuid)]);
      setMatches(m.slice(0, 3));
      setProposals(p);
    } catch (err) {
      setLead(null);
      fail(err, t('Could not load the request'));
    }
  }, [leadUuid, fail, t]);

  useEffect(() => {
    load();
  }, [load]);

  // The AI engine refines the triage in place shortly after submit (the queue
  // pickup alone can take most of a minute); poll until it settles - matches
  // depend on it - and stop after two minutes either way.
  const aiPending = lead?.triage?.model_meta?.ai_status === 'pending';
  const [pollsLeft, setPollsLeft] = useState(40);
  useEffect(() => {
    if (!aiPending || pollsLeft <= 0) return undefined;
    const timer = window.setTimeout(() => {
      setPollsLeft((n) => n - 1);
      load();
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [aiPending, pollsLeft, load]);

  const doAccept = async (proposal: Proposal) => {
    setAccepting(proposal.uuid);
    try {
      // The response's `warnings` are follow-ups for the LAWYER (they get them
      // in their notification) - never shown to the client.
      await acceptProposal(proposal.uuid);
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', duration: 4000, message: t('Proposal accepted') }));
      await load();
    } catch (err) {
      fail(err, t('Could not accept the proposal'));
      // A refused accept may have declined that proposal (e.g. a conflict): show it.
      load();
    } finally {
      setAccepting(null);
    }
  };

  const confirmAccept = (proposal: Proposal) =>
    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        title: t('Accept proposal'),
        message: t('Accept the proposal from {{name}}?', { name: proposal.lawyer_display_name }),
        subText: t('You hire {{name}} at the agreed price. They receive your description and documents. Other proposals are declined.', { name: proposal.lawyer_display_name }),
        icon: <PiHandshakeLight className="w-6 h-6 text-success-600" />,
        variant: 'success',
        confirmLabel: t('Accept'),
        onConfirm: () => doAccept(proposal),
      })
    );

  const engaged = lead?.status === 'engaged';
  const hiredLawyer = proposals?.find((p) => p.status === 'accepted')?.lawyer_display_name;

  // Client portal: once hired, the engagement (not the lawyer's matter) is what
  // the client follows here.
  const [engagement, setEngagement] = useState<EngagementView | null>(null);
  useEffect(() => {
    if (!engaged) return;
    getEngagement(leadUuid, 'client').then(setEngagement).catch(() => setEngagement(null));
  }, [engaged, leadUuid]);
  const fee = engagement?.fee_terms;

  return (
    <DetailFrame
      breadcrumbs={[
        { name: t('Marketplace'), href: '/marketplace', current: false },
        { name: lead ? leadTitle(t, lead) : t('Request'), href: `/marketplace/requests/${leadUuid}`, current: true },
      ]}
      onBack={() => history.push('/marketplace')}
    >
      <div className="details-canvas h-full w-full overflow-y-auto custom-scrollbar-main" data-testid="marketplace-request-detail">
        {lead === undefined ? (
          <Skeleton variant="detail" />
        ) : lead === null ? (
          <EmptyState headline={t('Request not found')} subtext={t('It may have been removed, or you no longer have access.')} />
        ) : (
          <div className="details-pad w-full space-y-7 px-6 py-6">
            {/* The matter itself is the lawyer's (restricted to them and their
                firm's admins); the client follows it here until the client portal. */}
            {engaged && (
              <GlassCard tone="success" icon={<PiHandshakeLight />} title={t('Lawyer hired')}>
                <div className="space-y-3">
                  <p className="text-sm text-fg-body" data-testid="marketplace-hired">
                    {hiredLawyer
                      ? t('{{name}} now has your description and documents and will contact you.', { name: hiredLawyer })
                      : t('Your lawyer now has your description and documents and will contact you.')}
                  </p>
                  {engagement && (
                    <div className="space-y-1.5" data-testid="marketplace-engagement">
                      <InfoRow label={t('Your lawyer')}
                        value={engagement.lawyer.firm_name
                          ? `${engagement.lawyer.display_name} · ${engagement.lawyer.firm_name}`
                          : engagement.lawyer.display_name} />
                      {fee?.amount && fee.currency && fee.fee_basis && (
                        <InfoRow label={t('Agreed price')}
                          value={`${enumLabel(t, fee.fee_basis)} · ${fmtProposalPrice(t, {
                            fee_basis: fee.fee_basis, amount: fee.amount, currency: fee.currency })}`} />
                      )}
                      <InfoRow label={t('Since')} value={fmtDate(engagement.started_at)} />
                    </div>
                  )}
                </div>
              </GlassCard>
            )}
            {engaged && engagement && (
              <Section title={t('Engagement letter')}>
                <EngagementLetter leadUuid={leadUuid} as="client" />
              </Section>
            )}
            {engaged && engagement && (
              <Section title={t('Messages with your lawyer')}>
                <EngagementConversation leadUuid={leadUuid} as="client" canMessage={engagement.can_message}
                  otherName={engagement.lawyer.display_name} />
              </Section>
            )}
            {engaged && engagement && (
              <Section title={t('Documents')}>
                <EngagementDocuments leadUuid={leadUuid} as="client" canAdd={engagement.can_message} />
              </Section>
            )}

            <Section title={t('Your request')}>
              <p className="whitespace-pre-wrap break-words text-[15px] leading-relaxed text-fg-body">{lead.issue_text}</p>
              {lead.documents.length > 0 && (
                <ul className="flex flex-wrap gap-2" data-testid="marketplace-request-documents">
                  {lead.documents.map((d) => (
                    <li key={d.file_uuid}>
                      <Well className="flex items-center gap-2 px-3 py-1.5">
                        <PiFileTextLight className="h-4 w-4 shrink-0 text-fg-muted" />
                        <span className="max-w-[16rem] truncate text-sm text-fg-body" title={d.name}>{d.name}</span>
                        {d.size != null && <span className="text-[11px] text-fg-muted">{fmtSize(d.size)}</span>}
                      </Well>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <div className="details-body items-start gap-6">
              <div className="min-w-0 space-y-7">
                <Section title={t('Proposals')} action={
                  <button type="button" onClick={load} className="text-[11px] text-fg-muted hover:text-fg-body">
                    {t('Refresh')}
                  </button>
                }>
                  <GlassCard padding="none">
                    {proposals === null ? (
                      <Skeleton variant="list" rows={2} />
                    ) : proposals.length === 0 ? (
                      <EmptyState density="compact" icon={<PiHandshakeLight size={28} />}
                        headline={t('No proposals yet')} subtext={t('Lawyers respond here with a price and a plan.')} />
                    ) : (
                      <ul className="divide-y divide-hairline-soft" data-testid="marketplace-proposals">
                        {proposals.map((p) => (
                          <li key={p.uuid} className="space-y-3 px-4 py-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="truncate text-sm text-fg-strong">{p.lawyer_display_name}</div>
                                <div className="text-[11px] text-fg-muted">
                                  {enumLabel(t, p.fee_basis)} · {fmtProposalPrice(t, p)}
                                </div>
                              </div>
                              <div className="flex shrink-0 items-center gap-2">
                                <ProposalStatusBadge status={p.status} />
                                {!engaged && isLive(p) && (
                                  <Button variant="primary" size="small" onClick={() => confirmAccept(p)}
                                    isLoading={accepting === p.uuid} disabled={accepting !== null}>
                                    {t('Accept')}
                                  </Button>
                                )}
                              </div>
                            </div>
                            <p className="whitespace-pre-wrap text-sm leading-relaxed text-fg-body">{p.plan_summary}</p>
                            {p.terms && (
                              <p className="whitespace-pre-wrap border-t border-hairline-soft pt-2 text-[11px] text-fg-muted">{p.terms}</p>
                            )}
                            <ProposalHistory proposal={p} viewer="client" />
                            {!engaged && isLive(p) && (
                              <ClientProposalActions proposal={p} disabled={accepting !== null}
                                onChanged={(next) => {
                                  setProposals((list) => list?.map((x) => (x.uuid === next.uuid ? next : x)) ?? list);
                                  load(); // the request may have reopened
                                }} />
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </GlassCard>
                </Section>

                <Section title={t('Suggested lawyers')}>
                  <GlassCard padding="none">
                    {matches === null ? (
                      <Skeleton variant="list" rows={3} />
                    ) : matches.length === 0 ? (
                      <EmptyState density="compact" icon={<PiUsersThreeLight size={28} />}
                        headline={t('No matching lawyers yet')} subtext={t('Lawyers who handle this type of issue in your country appear here.')} />
                    ) : (
                      <ul className="divide-y divide-hairline-soft" data-testid="marketplace-matches">
                        {matches.map((m) => (
                          <li key={m.lawyer_profile_uuid} className="flex items-start justify-between gap-3 px-4 py-3">
                            <div className="min-w-0">
                              <div className="truncate text-sm text-fg-strong">{m.display_name}</div>
                              {m.reasons.length > 0 && (
                                <div className="text-[11px] text-fg-muted">{m.reasons.join(' · ')}</div>
                              )}
                            </div>
                            <span className="shrink-0 text-[11px] text-fg-muted">
                              {t('Match {{score}}%', { score: Math.round(m.score * 100) })}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </GlassCard>
                </Section>
              </div>

              <div className="details-side min-w-0 space-y-7">
                {lead.triage && (
                  <Section title={t('Category')}>
                    <TriageSummary triage={lead.triage} audience="client" />
                  </Section>
                )}
                <Section title={t('Details')}>
                  <GlassCard>
                    <div className="space-y-2">
                      <InfoRow label={t('Status')}><LeadStatusBadge status={lead.status} audience="client" /></InfoRow>
                      <InfoRow label={t('Created')} value={fmtDate(lead.created_at)} />
                      <InfoRow label={t('Proposals')} value={String(proposals?.length ?? 0)} />
                    </div>
                  </GlassCard>
                </Section>
              </div>
            </div>
          </div>
        )}
      </div>
    </DetailFrame>
  );
};

const MarketplaceRequestDetailPage: React.FC = () => (
  <MarketplaceGate skeleton="detail">{() => <RequestDetailContent />}</MarketplaceGate>
);

export default MarketplaceRequestDetailPage;
